/**
 * Estate Flow - P&L and Tax Reports (Organization Level)
 * Professional Profit & Loss statements with expense entry
 * QuickBooks/Sage export capability
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import {
  Calculator, FileText, DollarSign, TrendingUp, TrendingDown,
  Calendar, Download, RefreshCw, Plus, Settings, PieChart,
  AlertCircle, CheckCircle, Globe, Receipt, Upload, FileSpreadsheet,
  Trash2, Edit, Eye, Filter, Search, Building2, Wallet
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Country flags
const FLAGS = {
  'BS': '🇧🇸', 'US': '🇺🇸', 'US-FL': '🇺🇸', 'GB': '🇬🇧', 'CA': '🇨🇦',
  'EU': '🇪🇺', 'JM': '🇯🇲', 'TT': '🇹🇹', 'BB': '🇧🇧', 'KY': '🇰🇾'
};

// Expense categories for property management
const EXPENSE_CATEGORIES = [
  { id: 'repairs', name: 'Repairs & Maintenance', icon: '🔧' },
  { id: 'utilities', name: 'Utilities', icon: '💡' },
  { id: 'insurance', name: 'Insurance', icon: '🛡️' },
  { id: 'property_tax', name: 'Property Taxes', icon: '🏛️' },
  { id: 'management_fees', name: 'Management Fees', icon: '📋' },
  { id: 'legal', name: 'Legal & Professional', icon: '⚖️' },
  { id: 'landscaping', name: 'Landscaping', icon: '🌿' },
  { id: 'cleaning', name: 'Cleaning & Janitorial', icon: '🧹' },
  { id: 'security', name: 'Security', icon: '🔒' },
  { id: 'pest_control', name: 'Pest Control', icon: '🐜' },
  { id: 'supplies', name: 'Office Supplies', icon: '📎' },
  { id: 'vendor', name: 'Vendor Services', icon: '🏢' },
  { id: 'special_assessment', name: 'Special Assessment', icon: '⚠️' },
  { id: 'capital_improvement', name: 'Capital Improvement', icon: '🏗️' },
  { id: 'other', name: 'Other Expense', icon: '📦' }
];

const INCOME_CATEGORIES = [
  { id: 'rent', name: 'Rental Income', icon: '🏠' },
  { id: 'hoa_fees', name: 'HOA Fees', icon: '🏢' },
  { id: 'late_fees', name: 'Late Fees', icon: '⏰' },
  { id: 'parking', name: 'Parking Income', icon: '🅿️' },
  { id: 'laundry', name: 'Laundry/Amenities', icon: '🧺' },
  { id: 'application_fees', name: 'Application Fees', icon: '📝' },
  { id: 'special_assessment', name: 'Special Assessment Income', icon: '💰' },
  { id: 'other', name: 'Other Income', icon: '💵' }
];

const PLReports = ({ orgId, orgName, orgCountry }) => {
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('expenses');
  const [taxConfig, setTaxConfig] = useState(null);
  const [jurisdictions, setJurisdictions] = useState({});
  const [usStates, setUsStates] = useState({});
  const [financialRecords, setFinancialRecords] = useState([]);
  const [savedReports, setSavedReports] = useState([]);

  // Report generation state
  const [reportPeriod, setReportPeriod] = useState({
    type: 'quarter',
    year: new Date().getFullYear(),
    quarter: Math.ceil((new Date().getMonth() + 1) / 3),
    startDate: '',
    endDate: ''
  });
  const [generatedReport, setGeneratedReport] = useState(null);

  // Entry dialogs
  const [expenseDialog, setExpenseDialog] = useState(false);
  const [incomeDialog, setIncomeDialog] = useState(false);
  const [configDialog, setConfigDialog] = useState(false);
  const [exportDialog, setExportDialog] = useState(false);

  // Form states
  const [expenseForm, setExpenseForm] = useState({
    category: '',
    vendor_name: '',
    description: '',
    amount: '',
    date: new Date().toISOString().split('T')[0],
    invoice_number: '',
    payment_method: 'check',
    notes: ''
  });

  const [incomeForm, setIncomeForm] = useState({
    category: '',
    description: '',
    amount: '',
    date: new Date().toISOString().split('T')[0],
    reference: '',
    notes: ''
  });

  const [configForm, setConfigForm] = useState({
    tax_applicable: 'no',
    country_code: orgCountry || 'US',
    state_code: '',
    tax_registration_number: '',
    filing_frequency: 'quarterly'
  });

  // Filter state
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterDateRange, setFilterDateRange] = useState('all');

  useEffect(() => {
    if (orgId) {
      fetchData();
    }
  }, [orgId]);

  useEffect(() => {
    const refreshFinancials = () => {
      if (orgId) fetchData();
    };
    window.addEventListener('estateflow:financials-updated', refreshFinancials);
    return () => window.removeEventListener('estateflow:financials-updated', refreshFinancials);
  }, [orgId]);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('access_token');
    return { Authorization: `Bearer ${token}` };
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const headers = getAuthHeaders();

      const [jurisdictionsRes, configRes, recordsRes, reportsRes] = await Promise.all([
        axios.get(`${API}/tax/jurisdictions`, { headers }).catch(() => ({ data: {} })),
        axios.get(`${API}/tax/config/${orgId}`, { headers }).catch(() => ({ data: {} })),
        axios.get(`${API}/tax/records/${orgId}`, { headers }).catch(() => ({ data: { records: [] } })),
        axios.get(`${API}/tax/reports/${orgId}`, { headers }).catch(() => ({ data: { reports: [] } }))
      ]);

      setJurisdictions(jurisdictionsRes.data?.jurisdictions || {});
      setUsStates(jurisdictionsRes.data?.us_states || {});
      setTaxConfig(configRes.data || {});
      setFinancialRecords(recordsRes.data?.records || []);
      setSavedReports(reportsRes.data?.reports || []);

      if (configRes.data) {
        setConfigForm({
          tax_applicable: configRes.data.tax_applicable || 'no',
          country_code: configRes.data.country_code || orgCountry || 'US',
          state_code: configRes.data.state_code || '',
          tax_registration_number: configRes.data.tax_registration_number || '',
          filing_frequency: configRes.data.filing_frequency || 'quarterly'
        });
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const addExpense = async () => {
    if (!expenseForm.category || !expenseForm.amount || !expenseForm.date) {
      toast.error('Please fill in required fields');
      return;
    }

    try {
      const headers = getAuthHeaders();
      await axios.post(`${API}/tax/expense?org_id=${orgId}`, {
        ...expenseForm,
        amount: parseFloat(expenseForm.amount),
        tax_deductible: true
      }, { headers });

      toast.success('Expense added successfully');
      setExpenseDialog(false);
      setExpenseForm({
        category: '',
        vendor_name: '',
        description: '',
        amount: '',
        date: new Date().toISOString().split('T')[0],
        invoice_number: '',
        payment_method: 'check',
        notes: ''
      });
      fetchData();
    } catch (error) {
      toast.error('Failed to add expense');
    }
  };

  const addIncome = async () => {
    if (!incomeForm.category || !incomeForm.amount || !incomeForm.date) {
      toast.error('Please fill in required fields');
      return;
    }

    try {
      const headers = getAuthHeaders();
      await axios.post(`${API}/tax/income?org_id=${orgId}`, {
        ...incomeForm,
        amount: parseFloat(incomeForm.amount),
        tax_applicable: true
      }, { headers });

      toast.success('Income recorded successfully');
      setIncomeDialog(false);
      setIncomeForm({
        category: '',
        description: '',
        amount: '',
        date: new Date().toISOString().split('T')[0],
        reference: '',
        notes: ''
      });
      fetchData();
    } catch (error) {
      toast.error('Failed to record income');
    }
  };

  const saveTaxConfig = async () => {
    try {
      const headers = getAuthHeaders();
      await axios.post(`${API}/tax/config`, {
        org_id: orgId,
        ...configForm
      }, { headers });

      toast.success('Tax configuration saved');
      setConfigDialog(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to save configuration');
    }
  };

  const generateReport = async () => {
    try {
      setLoading(true);
      const headers = getAuthHeaders();

      let startDate, endDate;
      if (reportPeriod.type === 'quarter') {
        const quarterMonths = { 1: ['01', '03'], 2: ['04', '06'], 3: ['07', '09'], 4: ['10', '12'] };
        const [startMonth, endMonth] = quarterMonths[reportPeriod.quarter];
        startDate = `${reportPeriod.year}-${startMonth}-01`;
        const lastDay = new Date(reportPeriod.year, parseInt(endMonth), 0).getDate();
        endDate = `${reportPeriod.year}-${endMonth}-${lastDay}`;
      } else {
        startDate = reportPeriod.startDate;
        endDate = reportPeriod.endDate;
      }

      const response = await axios.post(`${API}/tax/report/pl`, {
        org_id: orgId,
        start_date: startDate,
        end_date: endDate
      }, { headers });

      setGeneratedReport(response.data);
      setActiveTab('report');
      toast.success('P&L Report generated');
      fetchData();
    } catch (error) {
      toast.error('Failed to generate report');
    } finally {
      setLoading(false);
    }
  };

  // Export functions
  const exportToCSV = () => {
    if (!generatedReport) return;

    let csv = 'PROFIT & LOSS STATEMENT\n';
    csv += `Organization: ${orgName}\n`;
    csv += `Period: ${generatedReport.period?.start_date} to ${generatedReport.period?.end_date}\n`;
    csv += `Generated: ${new Date().toISOString()}\n\n`;

    csv += 'INCOME\n';
    csv += 'Category,Amount\n';
    generatedReport.income?.by_category?.forEach(cat => {
      csv += `${cat.category},${cat.total}\n`;
    });
    csv += `Total Income,${generatedReport.income?.total || 0}\n\n`;

    csv += 'EXPENSES\n';
    csv += 'Category,Amount\n';
    generatedReport.expenses?.by_category?.forEach(cat => {
      csv += `${cat.category},${cat.total}\n`;
    });
    csv += `Total Expenses,${generatedReport.expenses?.total || 0}\n\n`;

    csv += 'SUMMARY\n';
    csv += `Gross Income,${generatedReport.summary?.gross_income || 0}\n`;
    csv += `Total Expenses,${generatedReport.summary?.total_expenses || 0}\n`;
    csv += `Net Profit,${generatedReport.summary?.net_profit || 0}\n`;

    downloadFile(csv, `PL_Report_${orgName}_${reportPeriod.year}_Q${reportPeriod.quarter}.csv`, 'text/csv');
    toast.success('CSV exported for QuickBooks/Sage import');
  };

  const exportToQuickBooksIIF = () => {
    if (!generatedReport) return;

    // QuickBooks IIF format
    let iif = '!TRNS\tTRNSID\tTRNSTYPE\tDATE\tACCNT\tNAME\tAMOUNT\tMEMO\n';
    iif += '!SPL\tSPLID\tTRNSTYPE\tDATE\tACCNT\tNAME\tAMOUNT\tMEMO\n';
    iif += '!ENDTRNS\n';

    const date = generatedReport.period?.end_date || new Date().toISOString().split('T')[0];

    // Income entries
    generatedReport.income?.by_category?.forEach((cat, idx) => {
      iif += `TRNS\t${idx + 1}\tGENERAL JOURNAL\t${date}\tIncome:${cat.category}\t\t${cat.total}\tP&L Import\n`;
      iif += `SPL\t${idx + 1}\tGENERAL JOURNAL\t${date}\tBank Account\t\t-${cat.total}\tP&L Import\n`;
      iif += 'ENDTRNS\n';
    });

    // Expense entries
    generatedReport.expenses?.by_category?.forEach((cat, idx) => {
      const id = idx + 100;
      iif += `TRNS\t${id}\tGENERAL JOURNAL\t${date}\tExpense:${cat.category}\t\t${cat.total}\tP&L Import\n`;
      iif += `SPL\t${id}\tGENERAL JOURNAL\t${date}\tBank Account\t\t-${cat.total}\tP&L Import\n`;
      iif += 'ENDTRNS\n';
    });

    downloadFile(iif, `PL_QuickBooks_${orgName}_${reportPeriod.year}_Q${reportPeriod.quarter}.iif`, 'text/plain');
    toast.success('QuickBooks IIF file exported');
  };

  const downloadFile = (content, filename, type) => {
    const blob = new Blob([content], { type });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Calculate totals
  const totalExpenses = financialRecords
    .filter(r => r.record_type === 'expense')
    .reduce((sum, r) => sum + (r.amount || 0), 0);

  const totalIncome = financialRecords
    .filter(r => r.record_type === 'income')
    .reduce((sum, r) => sum + (r.amount || 0), 0);

  const jurisdiction = jurisdictions[configForm.country_code] || {};

  if (loading && !taxConfig) {
    return (
      <div className="flex justify-center items-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center">
            <Calculator className="h-6 w-6 mr-2 text-emerald-600" />
            Financial Management
          </h2>
          <p className="text-gray-500 mt-1">
            {orgName} • {FLAGS[configForm.country_code] || '🌍'} {jurisdiction?.name || configForm.country_code}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button onClick={() => setExpenseDialog(true)} className="bg-red-600 hover:bg-red-700">
            <Plus className="h-4 w-4 mr-2" />
            Add Expense
          </Button>
          <Button onClick={() => setIncomeDialog(true)} className="bg-emerald-600 hover:bg-emerald-700">
            <Plus className="h-4 w-4 mr-2" />
            Record Income
          </Button>
          <Button onClick={() => setConfigDialog(true)} variant="outline">
            <Settings className="h-4 w-4 mr-2" />
            Tax Settings
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="border-l-4 border-l-emerald-500">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Total Income</p>
                <p className="text-2xl font-bold text-emerald-600">{formatCurrency(totalIncome)}</p>
              </div>
              <div className="h-12 w-12 bg-emerald-100 rounded-full flex items-center justify-center">
                <TrendingUp className="h-6 w-6 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-red-500">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Total Expenses</p>
                <p className="text-2xl font-bold text-red-600">{formatCurrency(totalExpenses)}</p>
              </div>
              <div className="h-12 w-12 bg-red-100 rounded-full flex items-center justify-center">
                <TrendingDown className="h-6 w-6 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Net Profit/Loss</p>
                <p className={`text-2xl font-bold ${totalIncome - totalExpenses >= 0 ? 'text-blue-600' : 'text-amber-600'}`}>
                  {formatCurrency(totalIncome - totalExpenses)}
                </p>
              </div>
              <div className="h-12 w-12 bg-blue-100 rounded-full flex items-center justify-center">
                <Wallet className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-purple-500">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Tax Status</p>
                <p className="text-lg font-semibold text-purple-600">
                  {taxConfig?.tax_applicable === 'yes' ? 'Enabled' : 'Not Configured'}
                </p>
                <p className="text-xs text-gray-400">{jurisdiction?.tax_type || 'Configure tax settings'}</p>
              </div>
              <div className="h-12 w-12 bg-purple-100 rounded-full flex items-center justify-center">
                <FileText className="h-6 w-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-4 w-full max-w-lg">
          <TabsTrigger value="expenses">Expenses</TabsTrigger>
          <TabsTrigger value="income">Income</TabsTrigger>
          <TabsTrigger value="reports">Reports</TabsTrigger>
          <TabsTrigger value="report">P&L View</TabsTrigger>
        </TabsList>

        {/* Expenses Tab */}
        <TabsContent value="expenses">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="flex items-center">
                  <Receipt className="h-5 w-5 mr-2 text-red-500" />
                  Expense Records
                </CardTitle>
                <CardDescription>Track vendor costs, utilities, and other expenses</CardDescription>
              </div>
              <Button onClick={() => setExpenseDialog(true)} size="sm">
                <Plus className="h-4 w-4 mr-2" />
                Add Expense
              </Button>
            </CardHeader>
            <CardContent>
              {financialRecords.filter(r => r.record_type === 'expense').length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead>Vendor</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {financialRecords
                      .filter(r => r.record_type === 'expense')
                      .sort((a, b) => new Date(b.date) - new Date(a.date))
                      .slice(0, 20)
                      .map((record, idx) => (
                        <TableRow key={idx}>
                          <TableCell className="font-medium">{formatDate(record.date)}</TableCell>
                          <TableCell>
                            <Badge variant="outline" className="capitalize">
                              {record.category?.replace(/_/g, ' ')}
                            </Badge>
                          </TableCell>
                          <TableCell>{record.vendor_name || '-'}</TableCell>
                          <TableCell className="max-w-xs truncate">{record.description}</TableCell>
                          <TableCell className="text-right font-semibold text-red-600">
                            {formatCurrency(record.amount)}
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-12">
                  <Receipt className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500 mb-4">No expenses recorded yet</p>
                  <Button onClick={() => setExpenseDialog(true)}>
                    <Plus className="h-4 w-4 mr-2" />
                    Add Your First Expense
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Income Tab */}
        <TabsContent value="income">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="flex items-center">
                  <DollarSign className="h-5 w-5 mr-2 text-emerald-500" />
                  Income Records
                </CardTitle>
                <CardDescription>Track rent, fees, and other income sources</CardDescription>
              </div>
              <Button onClick={() => setIncomeDialog(true)} size="sm" className="bg-emerald-600 hover:bg-emerald-700">
                <Plus className="h-4 w-4 mr-2" />
                Record Income
              </Button>
            </CardHeader>
            <CardContent>
              {financialRecords.filter(r => r.record_type === 'income').length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead>Reference</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {financialRecords
                      .filter(r => r.record_type === 'income')
                      .sort((a, b) => new Date(b.date) - new Date(a.date))
                      .slice(0, 20)
                      .map((record, idx) => (
                        <TableRow key={idx}>
                          <TableCell className="font-medium">{formatDate(record.date)}</TableCell>
                          <TableCell>
                            <Badge className="bg-emerald-100 text-emerald-700 capitalize">
                              {record.category?.replace(/_/g, ' ')}
                            </Badge>
                          </TableCell>
                          <TableCell className="max-w-xs truncate">{record.description}</TableCell>
                          <TableCell>{record.reference || '-'}</TableCell>
                          <TableCell className="text-right font-semibold text-emerald-600">
                            {formatCurrency(record.amount)}
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-12">
                  <DollarSign className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500 mb-4">No income recorded yet</p>
                  <Button onClick={() => setIncomeDialog(true)} className="bg-emerald-600 hover:bg-emerald-700">
                    <Plus className="h-4 w-4 mr-2" />
                    Record Your First Income
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Reports Tab */}
        <TabsContent value="reports">
          <div className="grid md:grid-cols-2 gap-6">
            {/* Generate Report Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <FileText className="h-5 w-5 mr-2 text-blue-500" />
                  Generate P&L Report
                </CardTitle>
                <CardDescription>Create profit & loss statement for tax filing</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-2">
                  <Button
                    variant={reportPeriod.type === 'quarter' ? 'default' : 'outline'}
                    onClick={() => setReportPeriod({ ...reportPeriod, type: 'quarter' })}
                    className="flex-1"
                    size="sm"
                  >
                    Quarterly
                  </Button>
                  <Button
                    variant={reportPeriod.type === 'custom' ? 'default' : 'outline'}
                    onClick={() => setReportPeriod({ ...reportPeriod, type: 'custom' })}
                    className="flex-1"
                    size="sm"
                  >
                    Custom Range
                  </Button>
                </div>

                {reportPeriod.type === 'quarter' ? (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Year</Label>
                      <Select
                        value={reportPeriod.year.toString()}
                        onValueChange={(v) => setReportPeriod({ ...reportPeriod, year: parseInt(v) })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {[2024, 2025, 2026].map(y => (
                            <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">Quarter</Label>
                      <Select
                        value={reportPeriod.quarter.toString()}
                        onValueChange={(v) => setReportPeriod({ ...reportPeriod, quarter: parseInt(v) })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="1">Q1 (Jan-Mar)</SelectItem>
                          <SelectItem value="2">Q2 (Apr-Jun)</SelectItem>
                          <SelectItem value="3">Q3 (Jul-Sep)</SelectItem>
                          <SelectItem value="4">Q4 (Oct-Dec)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Start Date</Label>
                      <Input
                        type="date"
                        value={reportPeriod.startDate}
                        onChange={(e) => setReportPeriod({ ...reportPeriod, startDate: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label className="text-xs">End Date</Label>
                      <Input
                        type="date"
                        value={reportPeriod.endDate}
                        onChange={(e) => setReportPeriod({ ...reportPeriod, endDate: e.target.value })}
                      />
                    </div>
                  </div>
                )}

                <Button onClick={generateReport} className="w-full" disabled={loading}>
                  <FileText className="h-4 w-4 mr-2" />
                  Generate Report
                </Button>
              </CardContent>
            </Card>

            {/* Export Options */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Download className="h-5 w-5 mr-2 text-purple-500" />
                  Export to Accounting Software
                </CardTitle>
                <CardDescription>Download in QuickBooks or Sage compatible format</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <Button 
                  onClick={exportToCSV} 
                  variant="outline" 
                  className="w-full justify-start"
                  disabled={!generatedReport}
                >
                  <FileSpreadsheet className="h-4 w-4 mr-3 text-green-600" />
                  <div className="text-left">
                    <p className="font-medium">Export as CSV</p>
                    <p className="text-xs text-gray-500">Compatible with QuickBooks, Sage, Excel</p>
                  </div>
                </Button>

                <Button 
                  onClick={exportToQuickBooksIIF} 
                  variant="outline" 
                  className="w-full justify-start"
                  disabled={!generatedReport}
                >
                  <FileText className="h-4 w-4 mr-3 text-blue-600" />
                  <div className="text-left">
                    <p className="font-medium">QuickBooks IIF Format</p>
                    <p className="text-xs text-gray-500">Direct import to QuickBooks Desktop</p>
                  </div>
                </Button>

                {!generatedReport && (
                  <p className="text-sm text-amber-600 flex items-center">
                    <AlertCircle className="h-4 w-4 mr-2" />
                    Generate a report first to enable exports
                  </p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Saved Reports */}
          {savedReports.length > 0 && (
            <Card className="mt-6">
              <CardHeader>
                <CardTitle>Previous Reports</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {savedReports.slice(0, 5).map((report, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center">
                        <FileText className="h-4 w-4 mr-3 text-gray-400" />
                        <div>
                          <p className="font-medium text-sm">{report.report_id}</p>
                          <p className="text-xs text-gray-500">
                            {report.period?.start_date} to {report.period?.end_date}
                          </p>
                        </div>
                      </div>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => {
                          setGeneratedReport(report);
                          setActiveTab('report');
                        }}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* P&L Report View */}
        <TabsContent value="report">
          {generatedReport ? (
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Profit & Loss Statement</CardTitle>
                  <CardDescription>
                    {generatedReport.period?.start_date} to {generatedReport.period?.end_date}
                  </CardDescription>
                </div>
                <div className="flex gap-2">
                  <Button onClick={exportToCSV} variant="outline" size="sm">
                    <Download className="h-4 w-4 mr-2" />
                    CSV
                  </Button>
                  <Button onClick={exportToQuickBooksIIF} variant="outline" size="sm">
                    <Download className="h-4 w-4 mr-2" />
                    QuickBooks
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {/* Summary */}
                <div className="grid grid-cols-3 gap-4 mb-6 p-4 bg-gray-50 rounded-lg">
                  <div className="text-center">
                    <p className="text-sm text-gray-500">Gross Income</p>
                    <p className="text-2xl font-bold text-emerald-600">
                      {formatCurrency(generatedReport.summary?.gross_income)}
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="text-sm text-gray-500">Total Expenses</p>
                    <p className="text-2xl font-bold text-red-600">
                      {formatCurrency(generatedReport.summary?.total_expenses)}
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="text-sm text-gray-500">Net Profit</p>
                    <p className={`text-2xl font-bold ${generatedReport.summary?.net_profit >= 0 ? 'text-blue-600' : 'text-amber-600'}`}>
                      {formatCurrency(generatedReport.summary?.net_profit)}
                    </p>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  {/* Income Breakdown */}
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                      <TrendingUp className="h-4 w-4 mr-2 text-emerald-500" />
                      Income
                    </h4>
                    <div className="space-y-2">
                      {generatedReport.income?.by_category?.length > 0 ? (
                        generatedReport.income.by_category.map((cat, idx) => (
                          <div key={idx} className="flex justify-between items-center py-2 px-3 bg-emerald-50 rounded">
                            <span className="capitalize text-sm">{cat.category?.replace(/_/g, ' ')}</span>
                            <span className="font-medium text-emerald-700">{formatCurrency(cat.total)}</span>
                          </div>
                        ))
                      ) : (
                        <p className="text-gray-500 text-sm py-2">No income recorded</p>
                      )}
                      <div className="flex justify-between items-center py-2 px-3 bg-emerald-100 rounded font-semibold">
                        <span>Total Income</span>
                        <span className="text-emerald-700">{formatCurrency(generatedReport.income?.total)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Expense Breakdown */}
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                      <TrendingDown className="h-4 w-4 mr-2 text-red-500" />
                      Expenses
                    </h4>
                    <div className="space-y-2">
                      {generatedReport.expenses?.by_category?.length > 0 ? (
                        generatedReport.expenses.by_category.map((cat, idx) => (
                          <div key={idx} className="flex justify-between items-center py-2 px-3 bg-red-50 rounded">
                            <span className="capitalize text-sm">{cat.category?.replace(/_/g, ' ')}</span>
                            <span className="font-medium text-red-700">{formatCurrency(cat.total)}</span>
                          </div>
                        ))
                      ) : (
                        <p className="text-gray-500 text-sm py-2">No expenses recorded</p>
                      )}
                      <div className="flex justify-between items-center py-2 px-3 bg-red-100 rounded font-semibold">
                        <span>Total Expenses</span>
                        <span className="text-red-700">{formatCurrency(generatedReport.expenses?.total)}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Tax Info */}
                {generatedReport.tax_info && (
                  <div className="mt-6 p-4 bg-purple-50 rounded-lg border border-purple-200">
                    <h4 className="font-semibold text-purple-900 mb-2">Tax Calculation</h4>
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div>
                        <p className="text-purple-600">Jurisdiction</p>
                        <p className="font-medium">{generatedReport.tax_info.jurisdiction}</p>
                      </div>
                      <div>
                        <p className="text-purple-600">Tax Rate</p>
                        <p className="font-medium">{generatedReport.tax_info.rate}%</p>
                      </div>
                      <div>
                        <p className="text-purple-600">Estimated Tax</p>
                        <p className="font-medium">{formatCurrency(generatedReport.tax_info.estimated_tax)}</p>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="py-12 text-center">
                <FileText className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 mb-4">No report generated yet</p>
                <Button onClick={() => setActiveTab('reports')}>
                  Go to Reports Tab
                </Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Add Expense Dialog */}
      <Dialog open={expenseDialog} onOpenChange={setExpenseDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center">
              <Receipt className="h-5 w-5 mr-2 text-red-500" />
              Add Expense
            </DialogTitle>
            <DialogDescription>
              Enter vendor costs, utilities, or other expenses
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Category *</Label>
                <Select
                  value={expenseForm.category}
                  onValueChange={(v) => setExpenseForm({ ...expenseForm, category: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {EXPENSE_CATEGORIES.map((cat) => (
                      <SelectItem key={cat.id} value={cat.id}>
                        {cat.icon} {cat.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Date *</Label>
                <Input
                  type="date"
                  value={expenseForm.date}
                  onChange={(e) => setExpenseForm({ ...expenseForm, date: e.target.value })}
                />
              </div>
            </div>

            <div>
              <Label>Vendor Name</Label>
              <Input
                value={expenseForm.vendor_name}
                onChange={(e) => setExpenseForm({ ...expenseForm, vendor_name: e.target.value })}
                placeholder="e.g., ABC Plumbing, City Utilities"
              />
            </div>

            <div>
              <Label>Description *</Label>
              <Input
                value={expenseForm.description}
                onChange={(e) => setExpenseForm({ ...expenseForm, description: e.target.value })}
                placeholder="e.g., Roof repair, Monthly water bill"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Amount *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={expenseForm.amount}
                  onChange={(e) => setExpenseForm({ ...expenseForm, amount: e.target.value })}
                  placeholder="0.00"
                />
              </div>
              <div>
                <Label>Invoice #</Label>
                <Input
                  value={expenseForm.invoice_number}
                  onChange={(e) => setExpenseForm({ ...expenseForm, invoice_number: e.target.value })}
                  placeholder="INV-001"
                />
              </div>
            </div>

            <div>
              <Label>Payment Method</Label>
              <Select
                value={expenseForm.payment_method}
                onValueChange={(v) => setExpenseForm({ ...expenseForm, payment_method: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="check">Check</SelectItem>
                  <SelectItem value="card">Credit/Debit Card</SelectItem>
                  <SelectItem value="ach">Bank Transfer (ACH)</SelectItem>
                  <SelectItem value="cash">Cash</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Notes</Label>
              <Textarea
                value={expenseForm.notes}
                onChange={(e) => setExpenseForm({ ...expenseForm, notes: e.target.value })}
                placeholder="Additional notes..."
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setExpenseDialog(false)}>Cancel</Button>
            <Button onClick={addExpense} className="bg-red-600 hover:bg-red-700">
              Add Expense
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Income Dialog */}
      <Dialog open={incomeDialog} onOpenChange={setIncomeDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center">
              <DollarSign className="h-5 w-5 mr-2 text-emerald-500" />
              Record Income
            </DialogTitle>
            <DialogDescription>
              Enter rent payments, fees, or other income
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Category *</Label>
                <Select
                  value={incomeForm.category}
                  onValueChange={(v) => setIncomeForm({ ...incomeForm, category: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {INCOME_CATEGORIES.map((cat) => (
                      <SelectItem key={cat.id} value={cat.id}>
                        {cat.icon} {cat.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Date *</Label>
                <Input
                  type="date"
                  value={incomeForm.date}
                  onChange={(e) => setIncomeForm({ ...incomeForm, date: e.target.value })}
                />
              </div>
            </div>

            <div>
              <Label>Description *</Label>
              <Input
                value={incomeForm.description}
                onChange={(e) => setIncomeForm({ ...incomeForm, description: e.target.value })}
                placeholder="e.g., January rent - Unit 101"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Amount *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={incomeForm.amount}
                  onChange={(e) => setIncomeForm({ ...incomeForm, amount: e.target.value })}
                  placeholder="0.00"
                />
              </div>
              <div>
                <Label>Reference #</Label>
                <Input
                  value={incomeForm.reference}
                  onChange={(e) => setIncomeForm({ ...incomeForm, reference: e.target.value })}
                  placeholder="Check #, Transaction ID"
                />
              </div>
            </div>

            <div>
              <Label>Notes</Label>
              <Textarea
                value={incomeForm.notes}
                onChange={(e) => setIncomeForm({ ...incomeForm, notes: e.target.value })}
                placeholder="Additional notes..."
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIncomeDialog(false)}>Cancel</Button>
            <Button onClick={addIncome} className="bg-emerald-600 hover:bg-emerald-700">
              Record Income
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Tax Config Dialog */}
      <Dialog open={configDialog} onOpenChange={setConfigDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center">
              <Settings className="h-5 w-5 mr-2" />
              Tax Configuration
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label>Enable Tax Reporting</Label>
              <Select
                value={configForm.tax_applicable}
                onValueChange={(v) => setConfigForm({ ...configForm, tax_applicable: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="no">No</SelectItem>
                  <SelectItem value="yes">Yes</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Tax Jurisdiction</Label>
              <Select
                value={configForm.country_code}
                onValueChange={(v) => setConfigForm({ ...configForm, country_code: v, state_code: '' })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(jurisdictions).map(([code, info]) => (
                    <SelectItem key={code} value={code}>
                      {FLAGS[code]} {info.name} - {info.tax_type}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {(configForm.country_code === 'US' || configForm.country_code === 'US-FL') && (
              <div>
                <Label>US State</Label>
                <Select
                  value={configForm.state_code}
                  onValueChange={(v) => setConfigForm({ ...configForm, state_code: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select state" />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(usStates || {})
                      .sort((a, b) => a[1].name?.localeCompare(b[1].name))
                      .map(([code, info]) => (
                        <SelectItem key={code} value={code}>{info.name}</SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div>
              <Label>Tax Registration # (VAT/EIN/GST)</Label>
              <Input
                value={configForm.tax_registration_number}
                onChange={(e) => setConfigForm({ ...configForm, tax_registration_number: e.target.value })}
                placeholder="e.g., GB123456789"
              />
            </div>

            <div>
              <Label>Filing Frequency</Label>
              <Select
                value={configForm.filing_frequency}
                onValueChange={(v) => setConfigForm({ ...configForm, filing_frequency: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="quarterly">Quarterly</SelectItem>
                  <SelectItem value="annual">Annual</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setConfigDialog(false)}>Cancel</Button>
            <Button onClick={saveTaxConfig}>Save Configuration</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PLReports;
